import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  subscribeNotificationInsert,
  subscribeNotificationReconnect,
  subscribeNotificationUpdate,
} from "@/lib/notificationsBus";

const TYPES = [
  "lr_direct_join_request_accepted",
  "lr_direct_join_request_declined",
  // Legacy alias retained for backward compat with existing rows.
  "lr_direct_join_request_approved",
];
const TYPE_SET = new Set(TYPES);

/**
 * Tracks unread shipper responses to a broker's network join requests.
 * Toast + chime are owned by useGlobalNotificationAlerts.
 *
 * Self-heal: refetches on notifications-bus (re)connect and on window
 * focus / tab-visible, so responses that landed during a CHANNEL_ERROR
 * gap surface without a manual refresh.
 */
export function useUnreadJoinRequestResponses(userId: string | null | undefined) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!userId) { setCount(0); return; }
    let cancelled = false;

    const fetchCount = async () => {
      const { count: c } = await supabase
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .eq("recipient_user_id", userId)
        .in("type", TYPES)
        .eq("is_read", false);
      if (!cancelled) setCount(c ?? 0);
    };
    fetchCount();

    const offInsert = subscribeNotificationInsert(userId, (row) => {
      const t = (row as { type?: string })?.type;
      if (!t || !TYPE_SET.has(t)) return;
      setCount((c) => c + 1);
    });
    const offUpdate = subscribeNotificationUpdate(userId, () => fetchCount());
    const offReconnect = subscribeNotificationReconnect(userId, () => fetchCount());

    let timer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (timer) return;
      timer = setTimeout(() => { timer = null; fetchCount(); }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === "visible") runDebounced(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      offInsert();
      offUpdate();
      offReconnect();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      if (timer) clearTimeout(timer);
    };
  }, [userId]);

  return count;
}

/** Marks all unread join-request response notifications read for this broker. */
export async function markJoinRequestResponsesRead(userId: string) {
  await supabase
    .from("notifications")
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq("recipient_user_id", userId)
    .in("type", TYPES)
    .eq("is_read", false);
}
